import { useEffect, useMemo, useState } from "react";
import {
  fetchFPLData,
  fetchEliminatedByGW,
  fetchRemainingAfterGW,
  fetchEliminationRules,
  fetchManagerInfo,
  fetchFplLive,
  fetchEliminationCandidates,
} from "../api/fplApi";

const DEFAULT_LEAGUE_ID = "136323";
const stages = [
  { title: "STAGE 01", range: "GW 6–11", out: "5 OUT / GW", path: "136 → 106", from: 6, to: 11 },
  { title: "STAGE 02", range: "GW 12–18", out: "5 OUT / GW", path: "106 → 71", from: 12, to: 18 },
  { title: "STAGE 03", range: "GW 19–27", out: "4 OUT / GW", path: "71 → 35", from: 19, to: 27 },
  { title: "STAGE 04", range: "GW 28–37", out: "3 OUT / GW", path: "35 → 5", from: 28, to: 37 },
  { title: "THE FINAL", range: "GW 38", out: "4 OUT", path: "5 → 1", from: 38, to: 38 },
];

function stageForGW(gw) {
  const stage = stages.find((s) => gw >= s.from && gw <= s.to);
  return stage ? (stage.from === 38 ? "The Final" : `Stage ${stages.indexOf(stage) + 1}`) : null;
}

function Spinner({ message = "Loading the competition..." }) {
  return <div className="flex items-center justify-center gap-3 py-10 text-sm text-slate-500"><span className="h-5 w-5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />{message}</div>;
}

async function runWithLimit(tasks, limit = 4) {
  const results = new Array(tasks.length);
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    for (; i < tasks.length; ) {
      const cur = i++;
      try { results[cur] = await tasks[cur](); } catch { results[cur] = null; }
    }
  });
  await Promise.all(workers);
  return results;
}

// Captain details are presentation-only. The elimination result always comes
// from Supabase, regardless of whether this optional FPL request succeeds.
const captainCache = new Map();
function useEliminatedCaptain(entryId, gw) {
  const key = `${entryId}:${gw}`;
  const [captain, setCaptain] = useState(captainCache.get(key) ?? null);
  useEffect(() => {
    let mounted = true;
    const saved = captainCache.get(key);
    if (saved) { setCaptain(saved); return () => { mounted = false; }; }
    // Existing FPL Live backend returns squad names and the captain flag.
    fetchFplLive(entryId, gw)
      .then((data) => {
        const pick = data?.squad?.find((p) => p.is_captain);
        const result = pick?.name ? { name: pick.name, multiplier: data?.active_chip === "3xc" ? 3 : 2 } : { unavailable: true };
        captainCache.set(key, result);
        if (mounted) setCaptain(result);
      })
      .catch(() => { if (mounted) setCaptain({ unavailable: true }); });
    return () => { mounted = false; };
  }, [entryId, gw, key]);
  return captain;
}

function EliminatedRow({ gw, row, namesById, ensureName, index }) {
  const key = String(row.entry_id);
  const nameObj = namesById.get(key);
  const captain = useEliminatedCaptain(row.entry_id, gw);
  useEffect(() => { if (!nameObj) ensureName(row.entry_id); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const manager = nameObj?.manager_name?.trim() || row.manager_name || `Entry ${row.entry_id}`;
  const team = nameObj?.team_name?.trim() || row.team_name || "Unknown team";
  const points = Number(row.gw_points || 0);
  const hits = Number(row.hits || 0);
  return (
    <article className="group relative overflow-hidden rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 via-white to-red-50/80 shadow-sm transition hover:border-rose-400 hover:shadow-md">
      <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5 bg-rose-600" />
      <div className="flex flex-col gap-4 p-4 pl-5 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:p-5 sm:pl-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-rose-200 bg-white text-sm font-black text-rose-700">{String(index + 1).padStart(2, "0")}</span>
          <div className="min-w-0">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-white"><span aria-hidden="true">✕</span> ELIMINATED</span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-rose-500">Eliminated in GW{gw}</span>
            </div>
            <a className="block truncate text-sm font-extrabold text-slate-900 underline-offset-2 hover:text-rose-700 hover:underline" href={`https://fantasy.premierleague.com/entry/${row.entry_id}/event/${gw}`} target="_blank" rel="noopener noreferrer">{manager} ↗</a>
            <div className="mt-0.5 truncate text-xs text-slate-500">{team} · #{row.entry_id}</div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-slate-600">
              <span aria-hidden="true" className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-black">C</span>
              <span className="font-semibold">GW{gw} Captain:</span>
              <span>{captain?.name ? `${captain.name}${captain.multiplier === 3 ? " (Triple Captain)" : ""}` : captain?.unavailable ? "Not available" : "Loading…"}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 items-center gap-2 sm:flex sm:shrink-0 sm:gap-4">
          <div className="text-center"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">GW Pts</div><div className="mt-1 text-base font-extrabold text-slate-800">{points}</div></div>
          <div className="text-center"><div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Hits</div><div className="mt-1 text-base font-extrabold text-rose-700">{hits ? `−${hits}` : "0"}</div></div>
          <div className="rounded-xl border border-rose-200 bg-rose-100 px-4 py-2 text-center"><div className="text-[10px] font-black uppercase tracking-wide text-rose-700">Final score</div><div className="text-2xl font-black text-rose-700">{points - hits}</div></div>
        </div>
      </div>
    </article>
  );
}

function GWCard({ card, namesById, ensureName, featured = false }) {
  const { gw, dateLabel, eliminated, remainingCount, expectedElims } = card;
  const ranked = [...eliminated].sort((a,b) => (Number(a.gw_points||0) - Number(a.hits||0)) - (Number(b.gw_points||0) - Number(b.hits||0)));
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-5 sm:px-6 ${featured ? "bg-gradient-to-r from-slate-900 via-indigo-950 to-violet-900 text-white" : "bg-slate-50 text-slate-900"}`}>
        <div>
          <div className={`mb-1 text-[10px] font-black uppercase tracking-[.2em] ${featured ? "text-violet-200" : "text-violet-600"}`}>{featured ? "Latest elimination results" : "Previous round"} · {stageForGW(gw)}</div>
          <div className="flex items-baseline gap-3"><h3 className="text-2xl font-black">GAMEWEEK {gw}</h3><span className={`text-xs ${featured ? "text-violet-200" : "text-slate-500"}`}>{dateLabel}</span></div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-bold"><span className={`rounded-full px-3 py-2 ${featured ? "bg-white/15 text-white" : "bg-rose-100 text-rose-700"}`}>{eliminated.length}{typeof expectedElims === "number" ? ` / ${expectedElims}` : ""} eliminated</span><span className={`rounded-full px-3 py-2 ${featured ? "bg-emerald-400/20 text-emerald-200" : "bg-emerald-50 text-emerald-700"}`}>{remainingCount} survived</span></div>
      </div>
      <div className="space-y-2 bg-slate-50/70 p-3 sm:p-5">{ranked.length ? ranked.map((r,i)=><EliminatedRow key={`${gw}-${r.entry_id}`} gw={gw} row={r} namesById={namesById} ensureName={ensureName} index={i}/>) : <p className="rounded-xl bg-white py-8 text-center text-sm text-slate-500">Results are not saved for this round yet.</p>}</div>
    </section>
  );
}


// This is a read-only score comparison, fetched only when requested.
// Candidate API includes the people eliminated in this GW; remove them and
// every previously eliminated manager by intersecting with the survivor list.
function LowestSurvivors({ gw, remaining }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setOpen(false); setRows(null); setError(""); }, [gw]);
  const load = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (rows) return;
    setBusy(true); setError("");
    try {
      const result = await fetchEliminationCandidates(DEFAULT_LEAGUE_ID, gw, true);
      const validIds = new Set((remaining || []).map(m => Number(m.entry_id)));
      const eligible = (result.candidates || []).filter(m => validIds.has(Number(m.entry_id)));
      if (eligible.some(m => m.fetch_error)) throw new Error("Some GW scores are unavailable. Please try later.");
      setRows(eligible.sort((a,b) => Number(a.net_points)-Number(b.net_points) || Number(a.entry_id)-Number(b.entry_id)));
    } catch (err) { setError(err?.message || "Unable to load scores"); }
    finally { setBusy(false); }
  };
  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
      <div><div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Still in the competition</div>
        <h3 className="mt-1 text-base font-black text-slate-900">Lowest-scoring survivors · GW{gw}</h3>
        <p className="mt-1 text-xs text-slate-500">Compare the managers who narrowly avoided elimination. NET = GW points minus hits.</p>
      </div>
      <button type="button" onClick={load} disabled={busy} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-bold text-slate-800 hover:bg-slate-100 disabled:opacity-50">{busy ? "Loading scores…" : open ? "Hide scores ↑" : "View survivor scores ↓"}</button>
    </div>
    {open && <div className="border-t border-slate-100 px-5 pb-4 pt-3">
      {error ? <div className="text-sm text-rose-600">{error}</div> : busy ? <div className="text-sm text-slate-500">Calculating current survivors' GW scores…</div> : rows ? <>
        <div className="overflow-x-auto"><table className="w-full min-w-[460px] text-left text-xs"><thead className="border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="py-2">#</th><th>Manager / team</th><th className="text-right">GW pts</th><th className="text-right">Hits</th><th className="text-right">NET</th></tr></thead><tbody>{rows.slice(0, 20).map((m,i)=><tr key={m.entry_id} className="border-b border-slate-100 last:border-0"><td className="py-2.5 text-slate-400">{i+1}</td><td className="py-2"><a target="_blank" rel="noopener noreferrer" href={`https://fantasy.premierleague.com/entry/${m.entry_id}/event/${gw}`} className="font-bold text-slate-900 hover:text-violet-700">{m.manager_name || `Entry ${m.entry_id}`}</a><div className="text-[10px] text-slate-500">{m.team_name || "—"}</div></td><td className="text-right tabular-nums">{m.gw_points}</td><td className="text-right tabular-nums">{m.hits}</td><td className="text-right font-black tabular-nums text-emerald-700">{m.net_points}</td></tr>)}</tbody></table></div>
        <div className="pt-3 text-[11px] text-slate-500">Showing the lowest {Math.min(rows.length, 20)} of {rows.length} surviving managers. No eliminations are changed by this view.</div>
      </> : null}
    </div>}
  </section>;
}

export default function Elimination() {
  const [leagueId] = useState(DEFAULT_LEAGUE_ID);
  const [search, setSearch] = useState("");
  const [expandedHistory, setExpandedHistory] = useState(false);
  const [events, setEvents] = useState([]);       // bootstrap-static events
  const [latestFinished, setLatestFinished] = useState(0);

  // Map<string, {manager_name, team_name}>
  const [namesById, setNamesById] = useState(new Map());

  // right sidebar: full remaining after latest finished
  const [remainingNow, setRemainingNow] = useState({ count: 0, remaining: [] });
  const [loadingRemaining, setLoadingRemaining] = useState(false);

  // all GW cards
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);

  // ensureName: fetch on-demand and cache in namesById (keyed by String(id))
  const ensureName = async (entryId) => {
    const key = String(entryId);
    const existing = namesById.get(key);

    // Only skip if we already have a real name (not a placeholder)
    if (existing && existing.manager_name && !/^Entry\s/.test(existing.manager_name)) return;

    try {
      const info = await fetchManagerInfo(entryId); // {manager_name, team_name}
      if (info) {
        setNamesById((prev) => {
          const m = new Map(prev);
          m.set(key, {
            manager_name: info.manager_name || `Entry ${entryId}`,
            team_name: info.team_name || "",
          });
          return m;
        });
      }
    } catch {
      // don't cache a permanent placeholder on error; try again on next render if needed
    }
  };

  // 1) bootstrap + league standings (for names) + latest finished GW
  useEffect(() => {
    (async () => {
      try {
        const boot = await fetchFPLData();
        const evts = boot?.events || [];
        setEvents(evts);

        const finished = evts
          .filter((e) => e.finished || e.data_checked)
          .sort((a, b) => a.id - b.id)
          .pop();
        if (finished?.id) setLatestFinished(finished.id);

        // The backend uses Classic League 136323 as the roster source.
      } catch (e) {
        console.error("Initial load failed:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Elimination begins in GW6. Do not show GW1-5 cards.
  const gwsFinished = useMemo(() => {
    const last = Number(latestFinished || 0);
    if (last < 6) return [];
    return Array.from({ length: last - 5 }, (_, i) => i + 6);
  }, [latestFinished]);

  // helper: date under GW title
  const dateLabelForGW = (gw) => {
    const e = events.find((x) => x.id === gw);
    if (!e?.deadline_time) return "";
    try {
      const d = new Date(e.deadline_time);
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  // Build a single GW card
  const fetchGWCard = async (gw) => {
    const [elim, remaining, rule] = await Promise.all([
      fetchEliminatedByGW(gw).catch(() => []),
      fetchRemainingAfterGW(leagueId, gw).catch(() => ({ count: 0, remaining: [] })),
      fetchEliminationRules(gw).catch(() => null),
    ]);
    // fire & forget ensureName for any eliminated IDs we don't know yet
    (elim || []).forEach((r) => {
      const k = String(r.entry_id);
      if (!namesById.has(k)) ensureName(r.entry_id);
    });
    return {
      gw,
      dateLabel: dateLabelForGW(gw),
      eliminated: Array.isArray(elim) ? elim : [],
      remainingCount: Number(remaining?.count ?? 0),
      expectedElims: rule?.eliminate_count ?? null,
    };
  };

  // 2) Load only FINISHED GWs, newest first, with limited concurrency
  useEffect(() => {
    (async () => {
      if (!gwsFinished.length) return;
      setLoadingCards(true);
      try {
        const newestFirst = [...gwsFinished].sort((a, b) => b - a);
        const tasks = newestFirst.map((gw) => () => fetchGWCard(gw));
        const results = await runWithLimit(tasks, 4);
        // keep newest first in state
        setCards(results.filter(Boolean));
      } finally {
        setLoadingCards(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gwsFinished.length]);

  // 3) Remaining managers from the Classic League after the most recent finalized GW.
  useEffect(() => {
    (async () => {
      setLoadingRemaining(true);
      try {
        const rosterBoundaryGw = Number(latestFinished || 0) >= 6 ? Number(latestFinished) : 5;
        const rem = await fetchRemainingAfterGW(leagueId, rosterBoundaryGw);
        setRemainingNow(rem || { count: 0, remaining: [] });

        // ensure names for remaining too
        (rem?.remaining || []).forEach((r) => {
          const k = String(r.entry_id);
          if (!namesById.has(k)) {
            setNamesById((prev) => {
              const m = new Map(prev);
              m.set(k, {
                manager_name: r.manager_name || `Entry ${r.entry_id}`,
                team_name: r.team_name || "",
              });
              return m;
            });
          }
        });
      } finally {
        setLoadingRemaining(false);
      }
    })();
  }, [leagueId, latestFinished]); // namesById intentionally omitted

  const leftCards = useMemo(() => cards, [cards]);
  const latestCard = leftCards[0] || null;
  const olderCards = leftCards.slice(1);
  const shownCards = expandedHistory ? olderCards : olderCards.slice(0, 2);
  const rosterSize = 136;
  const remainingCount = Number(remainingNow?.count ?? 0);
  const outCount = Math.max(0, rosterSize - remainingCount);
  const completeWeeks = Math.max(0, Math.min(33, Number(latestFinished || 0) - 5));
  const progressPct = Math.round((completeWeeks / 33) * 100);
  const activeStage = stageForGW(Math.max(6, Number(latestFinished || 6))) || "Stage 1";
  const filteredRemaining = useMemo(() => (remainingNow?.remaining || []).filter((m) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [m.manager_name, m.team_name, m.entry_id].some((v) => String(v ?? "").toLowerCase().includes(term));
  }), [remainingNow, search]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 text-slate-900 sm:px-6">
      {/* Season hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#11172e] via-[#28205c] to-[#6237b5] p-6 text-white shadow-xl sm:p-9">
        <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full border-[55px] border-white/5" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 right-32 h-64 w-64 rounded-full bg-violet-400/10 blur-3xl" />
        <div className="relative">
          <div className="mb-3 flex flex-wrap items-center gap-2"><span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[.2em]">FPL NEPAL · SEASON 16</span><span className="rounded-full bg-violet-400/20 px-3 py-1 text-[10px] font-black tracking-widest text-violet-100">2026 / 27</span></div>
          <h1 className="max-w-2xl text-3xl font-black leading-tight tracking-tight sm:text-5xl">THE ROAD TO<br/><span className="text-violet-300">ONE CHAMPION.</span></h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-indigo-100">136 managers enter. One survives. Every finalized Gameweek, the lowest scores leave the competition.</p>
          <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "STARTING FIELD", value: "136" },
              { label: "STILL STANDING", value: loadingRemaining ? "—" : remainingCount },
              { label: "ELIMINATED", value: loadingRemaining ? "—" : outCount },
              { label: "LATEST FINALIZED", value: latestFinished >= 6 ? `GW ${latestFinished}` : "Awaiting GW6" },
            ].map((s)=><div key={s.label} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-sm"><div className="text-[10px] font-bold tracking-widest text-indigo-200">{s.label}</div><div className="mt-2 text-2xl font-black sm:text-3xl">{s.value}</div></div>)}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-[.2em] text-violet-600">Competition roadmap</div><h2 className="mt-1 text-xl font-black text-slate-900">The survival journey</h2></div><div className="rounded-full bg-violet-50 px-3 py-2 text-xs font-bold text-violet-700">{activeStage} · {completeWeeks} / 33 rounds complete</div></div>
        <div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500" style={{ width: `${progressPct}%` }}/></div>
        <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">{stages.map((s,i)=>{
          const active = Number(latestFinished || 6) >= s.from && Number(latestFinished || 6) <= s.to;
          const done = Number(latestFinished || 0) > s.to;
          return <div key={s.title} className={`rounded-2xl border p-4 ${active ? "border-violet-400 bg-violet-50 shadow-sm" : "border-slate-200 bg-slate-50/60"}`}>
            <div className="flex items-center justify-between gap-1"><span className={`text-[10px] font-black tracking-wider ${active ? "text-violet-700" : "text-slate-500"}`}>{s.title}</span><span className="text-xs">{done ? "✓" : active ? "●" : "○"}</span></div>
            <div className="mt-2 text-sm font-black">{s.range}</div><div className={`mt-2 text-sm font-extrabold ${active ? "text-violet-700" : "text-slate-700"}`}>{s.out}</div><div className="mt-2 text-xs text-slate-500">{s.path} managers</div>
          </div>;
        })}</div>
      </section>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <main className="space-y-5 lg:col-span-2">
          {latestCard ? <GWCard card={latestCard} featured namesById={namesById} ensureName={ensureName} /> : loadingCards ? <Spinner /> : <div className="rounded-3xl border border-violet-200 bg-violet-50 p-10 text-center"><div className="text-xl font-black text-violet-900">The competition begins at GW6</div><p className="mt-2 text-sm text-violet-700">All 136 managers remain in the competition until GW6 is finalized.</p></div>}
          {latestCard && latestCard.eliminated.length > 0 && !loadingRemaining && <LowestSurvivors gw={latestCard.gw} remaining={remainingNow?.remaining || []} />}
          {olderCards.length > 0 && <section className="space-y-4"><div className="flex items-center justify-between"><h2 className="text-lg font-black">Previous rounds</h2><span className="text-xs text-slate-500">Elimination archive</span></div>{shownCards.map((c)=><GWCard key={c.gw} card={c} namesById={namesById} ensureName={ensureName}/>)}{olderCards.length > 2 && <button type="button" onClick={()=>setExpandedHistory(v=>!v)} className="w-full rounded-xl border border-violet-200 bg-white px-4 py-3 text-sm font-bold text-violet-700 transition hover:bg-violet-50">{expandedHistory ? "Show fewer rounds ↑" : `View all ${olderCards.length} previous rounds ↓`}</button>}</section>}
          {loadingCards && latestCard && <Spinner message="Loading more gameweek results..." />}
        </main>

        <aside className="lg:sticky lg:top-5">
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="bg-slate-900 p-5 text-white"><div className="text-[10px] font-black uppercase tracking-[.2em] text-violet-300">Still in the race</div><div className="mt-1 flex items-end justify-between"><h2 className="text-xl font-black">Surviving managers</h2><span className="text-3xl font-black text-emerald-300">{loadingRemaining ? "—" : remainingCount}</span></div></div>
            <div className="p-4"><label className="sr-only" htmlFor="elimination-search">Search managers</label><input id="elimination-search" type="search" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search manager, team or ID..." className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100"/><div className="mt-3 flex items-center justify-between text-[11px] font-semibold text-slate-500"><span>{search ? `${filteredRemaining.length} matches` : "Current survivors"}</span><span>Classic League #136323</span></div></div>
            <div className="max-h-[640px] divide-y divide-slate-100 overflow-y-auto px-4 pb-4">{loadingRemaining ? <Spinner /> : filteredRemaining.length ? filteredRemaining.map((m,i)=>{const nm=namesById.get(String(m.entry_id));return <a key={m.entry_id} href={`https://fantasy.premierleague.com/entry/${m.entry_id}/event/${Math.max(1,latestFinished)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-1 py-3 transition hover:bg-violet-50"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-xs font-black text-violet-700">{String((m.manager_name || nm?.manager_name || "?").trim().charAt(0)).toUpperCase()}</span><div className="min-w-0 flex-1"><div className="truncate text-sm font-bold text-slate-900">{m.manager_name || nm?.manager_name || `Entry ${m.entry_id}`}</div><div className="truncate text-xs text-slate-500">{m.team_name || nm?.team_name || "—"}</div></div><span className="text-[10px] text-slate-400">#{m.entry_id}</span></a>}) : <p className="py-10 text-center text-sm text-slate-500">{search ? "No matching managers" : "No managers to display"}</p>}</div>
          </section>
        </aside>
      </div>
      <div className="pb-3 text-center text-xs text-slate-400">FPL NEPAL · SEASON 16 · ELIMINATION LEAGUE 2026/27</div>
    </div>
  );
}
